import ProductIdentifier from '@ingka/product-identifier';
import { translate } from '@repo/phrase';
import type { AssetArticle, AssetType } from '@repo/types/asset';
import { Paragraph } from '@repo/ui/typography';
import { concatNonEmptyStrings } from '@repo/utils';

import { getArticleNo, getAssetArticleKey } from '@/utils/article.utils';

import styles from './styles.module.scss';

interface AssetSummaryModalHeader {
    key: string;
    text: string;
}

export const tableHeader: AssetSummaryModalHeader[] = [
    { key: crypto.randomUUID(), text: 'summary_modal_article_table_article_number' },
    { key: crypto.randomUUID(), text: 'summary_modal_article_table_article' },
    { key: crypto.randomUUID(), text: 'summary_modal_article_table_quantity' },
    { key: crypto.randomUUID(), text: 'summary_modal_article_table_price' },
    { key: crypto.randomUUID(), text: 'summary_modal_article_table_total' },
];

interface Props {
    items: AssetArticle[];
    assetType: AssetType;
    orderType?: string | undefined;
}

interface ArticleCardItemProps {
    item: AssetArticle;
}

const ArticleCardItem = ({ item }: ArticleCardItemProps) => {
    if (!item.itemId) {
        return null;
    }

    const unitPrice = item.unitPrice?.toFixed(2);
    const totalPrice = item.totalPrice?.toFixed(2);

    return (
        <div data-testid="asset-summary-article-table-card" className={styles.asset_summary_article_table_row}>
            <div>
                <ProductIdentifier subtle value={getArticleNo(item.itemId)} />
            </div>
            <div>
                <Paragraph>{concatNonEmptyStrings([item.name, item.description])}</Paragraph>
            </div>
            <div>
                <Paragraph>{item.quantity}</Paragraph>
            </div>
            <div>
                <Paragraph>{unitPrice}</Paragraph>
            </div>
            <div>
                <Paragraph>{totalPrice}</Paragraph>
            </div>
        </div>
    );
};

export const AssetSummaryModalArticleTable = ({ items, assetType, orderType }: Props) => {
    return (
        <div
            data-testid={`summary-modal-article-table-${orderType ?? assetType}`}
            className={styles.asset_summary_article_table}
        >
            <div className={styles.asset_summary_article_table_header}>
                {tableHeader.map((headerItem) => (
                    <div key={headerItem.key}>
                        <Paragraph bodySize="s">{translate(headerItem.text)}</Paragraph>
                    </div>
                ))}
            </div>
            <div className={styles.asset_summary_table}>
                {items.map((item) => (
                    <ArticleCardItem item={item} key={getAssetArticleKey(item)} />
                ))}
            </div>
        </div>
    );
};
